/**
 * Assistant suggestion chips, ported from `OEM+_Platform_Demo.html`.
 *
 * Source lines
 * ------------
 * - `SUGG`  — demo line 4092 (16 view keys)
 * - `PSUGG` — demo line 4093 (6 persona keys)
 *
 * Raw data only. `reportSugg()` and `renderSugg()` — the functions that pick,
 * shuffle and paint these — are deliberately **not** ported; they are behaviour
 * and belong to the scene lane.
 *
 * What changed in the port
 * ------------------------
 * 1. **Machine ids stay symbolic.** Eight prompts interpolated `IDS.J07`,
 *    `IDS.T22` or `IDS.L14` — ids that only exist once the data pack is
 *    loaded. Each is a `{ machineAlias }` placeholder segment, so a prompt is
 *    a {@link SuggestionPrompt}: an ordered list of segments, with every piece
 *    of literal text copied character for character (note `A$520`, the `&` in
 *    "Load & Haul", and the ASCII apostrophes in "Kalbarri's" and "today's").
 *    Use {@link suggestionText} to flatten one against a pack's `IDS` map.
 *
 * 2. **Nothing else changed.** The demo carried these as plain template
 *    literals with no markup and no handler strings, so there was nothing to
 *    strip.
 *
 * Note that two view keys are exact duplicates in the demo: `documents`
 * repeats `data`, and `approvals` repeats one of `overview`'s prompts. Both
 * are reproduced as written.
 *
 * Data Schema layer — pure data and pure functions. No React, no side effects.
 */

import type { MachineAlias, MachineIdMap } from './modules';

/**
 * One piece of a suggestion prompt.
 *
 * - `{ text }` — literal copy from the demo, verbatim.
 * - `{ machineAlias }` — a machine id resolved through the pack's `IDS` map.
 */
export type SuggestionSegment =
  | { readonly text: string }
  | { readonly machineAlias: MachineAlias };

/** A suggestion chip, as an ordered list of segments. */
export type SuggestionPrompt = readonly SuggestionSegment[];

/** Literal text segment. */
export const sg = (text: string): SuggestionSegment => ({ text });

/** Machine-id placeholder segment. */
export const sgm = (machineAlias: MachineAlias): SuggestionSegment => ({ machineAlias });

/** Flatten a prompt against a data pack's `IDS` map. Pure. */
export const suggestionText = (prompt: SuggestionPrompt, ids: MachineIdMap): string =>
  prompt.map((s) => ('text' in s ? s.text : ids[s.machineAlias])).join('');

/** View keys the demo suggested against. */
export const SUGGESTION_VIEWS = [
  'overview',
  'drilling',
  'loadhaul',
  'reports',
  'approvals',
  'data',
  'fleet',
  'machine',
  'alerts',
  'incident',
  'workflows',
  'costs',
  'workorders',
  'documents',
  'connectors',
  'agents',
] as const;

/** Closed set of suggestion view keys. */
export type SuggestionView = (typeof SUGGESTION_VIEWS)[number];

/** Demo line 4092 — `const SUGG={…}`. */
export const SUGGESTIONS: Readonly<Record<SuggestionView, readonly SuggestionPrompt[]>> = {
  overview: [
    [sg('What needs a decision today?')],
    [sg('Generate the weekly status report for Load & Haul')],
    [sg('Which mine has the highest cost per operating hour and why?')],
  ],
  drilling: [
    [sg('Which drills are at risk this week?')],
    [
      sg('How confident is the '),
      sgm('J07'),
      sg(' prediction and what is the parts position?'),
    ],
  ],
  loadhaul: [
    [sg('Do we have brake pads for '), sgm('T22'), sg(' on site?')],
    [sg('Compare emissions by drive type')],
    [sg('Which loaders break down most?')],
  ],
  reports: [
    [sg('Write the machine risk email for Kalbarri')],
    [sg('Create the Broken Ridge equipment summary presentation')],
    [sg('Which machine in this report should I look at first?')],
  ],
  approvals: [
    [sg('What am I approving on '), sgm('T22'), sg(' and what happens if I wait?')],
    [sg('Is air freight for the '), sgm('J07'), sg(' kit worth A$520?')],
    [sg('Generate the weekly status report for Load & Haul')],
  ],
  data: [
    [sg('What does TB-2026-031 require sites to do?')],
    [sg('Find every mention of receiver key')],
  ],
  fleet: [
    [sg('Which machines are most likely to fail next and why?')],
    [sg('What is the breakdown trend across the six mines?')],
    [sg('Why is Kalbarri\'s cost per operating hour so high?')],
  ],
  machine: [
    [sg('Give me the full history of this machine')],
    [sg('What should we change next on it and what will it cost?')],
    [sg('Is its telemetry drifting?')],
  ],
  alerts: [
    [sg('How confident should I be in the '), sgm('J07'), sg(' prediction?')],
    [sg('Do we have brake pads for '), sgm('T22'), sg(' on site?')],
    [sg('What did the '), sgm('L14'), sg(' failure cost versus a planned change?')],
  ],
  incident: [
    [sg('What were the precursors to the '), sgm('L14'), sg(' failure?')],
    [sg('How many coupling failures have we had and where?')],
    [sg('Which crew is responsible for the most breakdown cost?')],
  ],
  workflows: [
    [sg('Which workflow steps are waiting on a human?')],
    [sg('What parts are holding up the Broken Ridge job?')],
  ],
  costs: [
    [sg('Compare cost per operating hour by mine and explain the difference')],
    [sg('Break down cost by crew at Kalbarri')],
    [sg('Which model is cheapest to run per hour?')],
  ],
  workorders: [
    [sg('Which open work orders are agent-drafted?')],
    [sg('Show breakdown trend for LHDs')],
  ],
  documents: [
    [sg('What does TB-2026-031 require sites to do?')],
    [sg('Find every mention of receiver key')],
  ],
  connectors: [[sg('Which mines have live data and what difference does it make?')]],
  agents: [[sg('What can agents do without approval at L2?')]],
};

/** Persona keys the demo suggested against. */
export const SUGGESTION_PERSONAS = [
  'rel',
  'rsm',
  'tech',
  'parts',
  'planner',
  'aftermarket',
] as const;

/** Closed set of suggestion persona keys. */
export type SuggestionPersona = (typeof SUGGESTION_PERSONAS)[number];

/** Demo line 4093 — `const PSUGG={…}`. */
export const PERSONA_SUGGESTIONS: Readonly<
  Record<SuggestionPersona, readonly SuggestionPrompt[]>
> = {
  rel: [[sg('Which components fail most across the fleet and what is the precursor?')]],
  rsm: [[sg('Write the machine risk email for Mount Isolde')]],
  tech: [[sg('What is on the '), sgm('L14'), sg(' follow-up and which procedure applies?')]],
  parts: [[sg('Which parts are below minimum at the Broken Ridge store?')]],
  planner: [[sg('What is due at Mount Isolde in the next 7 days?')]],
  aftermarket: [
    [sg('Generate today\'s S.O.S report')],
    [sg('Which technician should take the S-09 rotary head job and why?')],
    [sg('How did customers rate us this month?')],
  ],
};
